/**
 * Подтверждение после оплаты.
 *
 * Раньше экран сбрасывался мгновенно: чек ушёл в принтер, а кассир
 * оставался перед пустым заказом. Если гость спрашивал «сколько сдачи?»
 * или «а что вошло?», ответить было нечем — только искать в чеках.
 *
 * Теперь на несколько секунд остаётся итог: номер, сумма, сдача крупно
 * и состав. Сдача — самое большое на экране: её называют вслух,
 * и ошибиться тут дороже всего.
 *
 * Закрывается само через восемь секунд или по нажатию: очередь
 * не должна ждать, пока кассир дочитает.
 */
import { useEffect, useState } from 'react';
import { formatMoney, moneyInWords } from '@dastarhan/core';

export type PaidInfo = {
  number: number;
  total: number;
  /** сдача наличными; 0 — платили без сдачи или картой */
  change: number;
  positions: number;
  tableName?: string | null;
  /** оплата картой прошла, но лента могла не напечататься */
  printFailed?: boolean;
};

export function PaidScreen({ info, onClose, onCopy }: {
  info: PaidInfo;
  onClose: () => void;
  onCopy: () => void;
}) {
  const [left, setLeft] = useState(8);
  // Кассир коснулся экрана — значит, он читает или отвечает гостю.
  // Таймер останавливается: экран не исчезает посреди ответа.
  // Очередь не страдает: кто не читает — у того закроется само за 8 секунд
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const tick = setInterval(() => setLeft((v) => v - 1), 1000);
    return () => clearInterval(tick);
  }, [paused]);
  useEffect(() => {
    if (!paused && left <= 0) onClose();
  }, [left, paused, onClose]);

  return (
    <div className="paid-screen" onClick={onClose}>
      <div className="paid-card" onClick={(e) => e.stopPropagation()}
        onPointerDown={() => setPaused(true)}>
        <div className="paid-head">
          <span className="paid-mark">✓</span>
          <div>
            <h2>Чек №{info.number} закрыт</h2>
            <p className="paid-sub">
              {info.positions} поз.{info.tableName ? ` · стол ${info.tableName}` : ''}
            </p>
          </div>
        </div>

        <div className="paid-total">
          <span>Итого</span>
          <b>{formatMoney(info.total)}</b>
        </div>

        {info.change > 0 && (
          <div className="paid-change">
            <span>Сдача</span>
            <b>{formatMoney(info.change)}</b>
            {/* Пропись под цифрой: сдачу называют вслух, и «сорок пять»
                вместо «четыреста пятьдесят» — обычная ошибка в шуме зала */}
            <i>{moneyInWords(info.change)}</i>
          </div>
        )}

        {info.printFailed && (
          <p className="paid-warn">
            Чек не напечатался — оплата прошла. Нажмите «Ещё копию»
            или найдите чек в списке смены.
          </p>
        )}

        <div className="paid-actions">
          <button className="btn" onClick={(e) => { e.stopPropagation(); onCopy(); }}>
            Ещё копию
          </button>
          <button className="btn accent" onClick={onClose}>
            Дальше{!paused && left > 0 ? ` · ${left}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
